'use client';

import { useEffect, useRef, useId } from 'react';

interface StaticPageRendererProps {
  content: string;
  title?: string;
}

/**
 * Sanitize string to remove invalid characters that cause DOMException.
 * Same logic as WidgetRenderer.tsx HTMLWidget.
 */
function sanitizeForDOM(str: string): string {
  if (!str) return '';
  try {
    return str
      .replace(/[\x00-\x08\x0B\x0C\x0E-\x1F\x7F]/g, '')
      .replace(/[\x80-\x9F]/g, '')
      .replace(/￾|￿/g, '');
  } catch {
    return '';
  }
}

/**
 * StaticPageRenderer — Renders Quintype static page HTML content directly into
 * the page DOM (no iframe). Scripts inserted via innerHTML never execute, so
 * they're extracted and re-created manually, mirroring CustomAdWidget in
 * WidgetRenderer.tsx.
 */
export default function StaticPageRenderer({
  content,
  title,
}: StaticPageRendererProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const uniqueId = useId().replace(/:/g, '').replace(/^(\d)/, 'sp$1');
  const widgetId = `static-page-${uniqueId}`;

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const sanitizedHtml = sanitizeForDOM(content);

    // Rewrite hardcoded dinamani.com domain links in href attributes
    const currentOrigin =
      typeof window !== 'undefined' ? window.location.origin : '';
    const rewrittenHtml = currentOrigin
      ? sanitizedHtml.replace(
          /href\s*=\s*"(https?:\/\/(?:www\.)?dinamani\.com)([^"]*)"/gi,
          (_, _domain, path) => `href="${currentOrigin}${path}"`
        )
      : sanitizedHtml;

    // If content is a full HTML document, pull out head + body contents
    // (styles/links from head still work when placed anywhere in the DOM).
    const isFullDocument = /<!DOCTYPE|<html[\s>]/i.test(rewrittenHtml);
    let innerHtml = rewrittenHtml;

    if (isFullDocument) {
      const headMatch = rewrittenHtml.match(/<head[^>]*>([\s\S]*?)<\/head>/i);
      const bodyMatch = rewrittenHtml.match(/<body[^>]*>([\s\S]*)<\/body>/i);
      innerHtml =
        (headMatch ? headMatch[1] : '') +
        (bodyMatch ? bodyMatch[1] : rewrittenHtml);
    }

    // Parse in a detached container so embedded scripts don't get inserted
    // (and silently no-op'd) before we've had a chance to extract them.
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = innerHtml;

    const scripts = Array.from(tempDiv.querySelectorAll('script'));
    const scriptsToExecute: { src?: string; content?: string }[] = [];
    scripts.forEach((script) => {
      if (script.src) {
        scriptsToExecute.push({ src: script.src });
      } else if (script.textContent) {
        scriptsToExecute.push({ content: sanitizeForDOM(script.textContent) });
      }
      script.remove();
    });

    container.innerHTML = tempDiv.innerHTML;

    let cancelled = false;

    // Loads an external script and resolves only once it has actually run,
    // so scripts that depend on it (e.g. a jQuery plugin needing jQuery)
    // don't execute before it's ready.
    const loadScript = (src: string): Promise<void> =>
      new Promise((resolve) => {
        const existing = document.querySelector<HTMLScriptElement>(
          `script[src="${src}"]`
        );
        if (existing) {
          if (existing.dataset.loaded === 'true') {
            resolve();
          } else {
            existing.addEventListener('load', () => resolve(), {
              once: true,
            });
            existing.addEventListener('error', () => resolve(), {
              once: true,
            });
          }
          return;
        }
        const newScript = document.createElement('script');
        newScript.src = src;
        newScript.addEventListener(
          'load',
          () => {
            newScript.dataset.loaded = 'true';
            resolve();
          },
          { once: true }
        );
        newScript.addEventListener('error', () => resolve(), { once: true });
        document.body.appendChild(newScript);
      });

    const runInlineScript = (code: string) => {
      try {
        // By the time this script runs, the real DOMContentLoaded event has
        // long since fired (this executes post-hydration), so a widget script
        // that does `document.addEventListener('DOMContentLoaded', ...)` would
        // otherwise never run. Pass a `document` proxy that fires those
        // listeners immediately, since the DOM is already ready.
        const patchedDocument = new Proxy(document, {
          get(target, prop, receiver) {
            if (prop === 'addEventListener') {
              return (
                type: string,
                listener: EventListenerOrEventListenerObject,
                options?: boolean | AddEventListenerOptions
              ) => {
                if (
                  type === 'DOMContentLoaded' ||
                  type === 'readystatechange'
                ) {
                  setTimeout(() => {
                    const handler =
                      typeof listener === 'function'
                        ? listener
                        : listener.handleEvent.bind(listener);
                    handler(new Event(type));
                  }, 0);
                  return;
                }
                return target.addEventListener(type, listener, options);
              };
            }
            const value = Reflect.get(target, prop, receiver);
            return typeof value === 'function' ? value.bind(target) : value;
          },
        });
        const fn = new Function('document', code);
        fn(patchedDocument);
      } catch {
        try {
          const newScript = document.createElement('script');
          newScript.textContent = code;
          document.body.appendChild(newScript);
          newScript.remove();
        } catch {
          // Silently fail if script cannot be executed
        }
      }
    };

    // Execute scripts sequentially, in document order, awaiting each
    // external script's load before moving on — mirrors how a browser's
    // native HTML parser would run them (which is why this works in the
    // CMS preview but previously didn't on the live page).
    (async () => {
      for (const scriptInfo of scriptsToExecute) {
        if (cancelled) return;
        try {
          if (scriptInfo.src) {
            await loadScript(scriptInfo.src);
          } else if (scriptInfo.content) {
            runInlineScript(scriptInfo.content);
          }
        } catch {
          // Silently handle script creation/execution errors
        }
      }
    })();

    return () => {
      cancelled = true;
      container.innerHTML = '';
    };
  }, [content]);

  return (
    <div
      ref={containerRef}
      id={widgetId}
      aria-label={title}
      className="static-page-content"
      style={{ width: '100%', maxWidth: '100%' }}
    />
  );
}
